// shop.ts — the Truth Engine SPA. Client-routes off the URL path:
//   /shop                    → MacBook browse: left filters + search + sort + image cards
//   /shop/<category>         → same, scoped to a category
//   /shop/<category>/<slug>  → the product TRUTH page (offers ranked by trust,
//                              faults up front, claim-vs-reality, confidence)
// Reads the owned truth graph via /api/shop/*. No framework.

import { esc } from './ui.js';

const app = document.getElementById('app')!;

// ---- types mirroring src/lib/truth.ts read shapes --------------------------
interface CategoryItem {
  slug: string; name: string; brand: string; model: string; variant: string | null;
  image_url: string | null; msrp_usd: string | null; best_price: string | null;
  price_min: string | null; price_max: string | null; best_score: number | null; best_grade: string | null;
  offer_count: number; conditions: string[]; retailers: string[];
}
interface FaultView { category: string; severity: string; title: string; detail: string; evidence_url: string | null; verified: boolean; }
interface ClaimView { claim_text: string; claim_type: string; reality: string | null; delta: string; note: string | null; }
interface OfferView {
  id: string; retailer: string; title: string | null; condition: string; condition_grade: string | null;
  price_usd: string; list_price_usd: string | null; in_stock: boolean; seller: string | null; seller_rating: string | null;
  warranty: string | null; returns: string | null; shipping_usd: string | null; url: string; affiliate_url: string | null;
  crawl_source: string; source_detail: string | null; fetched_at: number; score: number | null; grade: string | null;
  faults: FaultView[]; claims: ClaimView[];
}
interface ProductView {
  id: string; slug: string; category: string; brand: string; model: string; variant: string | null; name: string;
  specs: Record<string, unknown> | null; summary: string | null; image_url: string | null; msrp_usd: string | null;
  release_date: string | null; offers: OfferView[]; best_price: string | null; lowest_trusted: OfferView | null;
}

const RETAILER_LABEL: Record<string, string> = {
  apple: 'Apple', amazon: 'Amazon', walmart: 'Walmart', ebay: 'eBay',
  bhphoto: 'B&H Photo', backmarket: 'Back Market', bestbuy: 'Best Buy',
};
const COND_LABEL: Record<string, string> = { new: 'New', refurb: 'Refurbished', open_box: 'Open box', used: 'Used', renewed: 'Renewed' };
const COND_ORDER = ['new', 'refurb', 'open_box', 'renewed', 'used'];
const DELTA_LABEL: Record<string, string> = { match: 'Accurate', overstated: 'Overstated', misleading: 'Misleading', false: 'False', unverified: 'Unverified' };
const GRADE_RANK: Record<string, number> = { A: 5, B: 4, C: 3, D: 2, F: 1 };

async function api<T>(path: string): Promise<T> {
  const r = await fetch(path);
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json() as Promise<T>;
}
function money(s: string | null): string { return s ? '$' + Number(s).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—'; }

// ---- router ----------------------------------------------------------------
async function route(): Promise<void> {
  const segs = location.pathname.split('/').filter(Boolean); // ['shop', cat?, slug?]
  try {
    if (segs.length >= 3) return await renderProduct(decodeURIComponent(segs[2]!));
    return await renderBrowse(segs.length === 2 ? decodeURIComponent(segs[1]!) : 'laptops');
  } catch {
    app.innerHTML = `<div class="loading">Couldn't load that. <a href="/shop" data-spa>Back to shop</a></div>`;
  }
}
function go(href: string): void { history.pushState(null, '', href); route(); }
addEventListener('popstate', route);
document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[data-spa]') as HTMLAnchorElement | null;
  if (a && a.href) { e.preventDefault(); go(new URL(a.href).pathname); }
});

// ---- browse: filters + search + sort + image grid --------------------------
interface FilterState { q: string; sort: string; conditions: Set<string>; retailers: Set<string>; maxPrice: number; minGrade: string; }

async function renderBrowse(category: string): Promise<void> {
  const { products } = await api<{ products: CategoryItem[] }>(`/api/shop/category/${encodeURIComponent(category)}`);
  const allConds = Array.from(new Set(products.flatMap(p => p.conditions))).sort((a, b) => COND_ORDER.indexOf(a) - COND_ORDER.indexOf(b));
  const allRetailers = Array.from(new Set(products.flatMap(p => p.retailers))).sort();
  const priceCeil = Math.ceil(Math.max(100, ...products.map(p => Number(p.price_max || p.best_price || 0))) / 100) * 100;

  const state: FilterState = { q: '', sort: 'trusted', conditions: new Set(), retailers: new Set(), maxPrice: priceCeil, minGrade: '' };

  app.innerHTML = `
    <header class="shop-hero compact">
      <h1>Shop ${esc(category)} — honestly.</h1>
      <p class="lede">Every offer, every retailer, ranked by <strong>trust</strong> not price. Faults exposed up front. ${products.length} models · ${products.reduce((n, p) => n + p.offer_count, 0)} offers compared.</p>
    </header>
    <div class="browse">
      <aside class="filters" id="filters">
        <div class="f-search"><input id="q" type="search" placeholder="Search MacBooks…" autocomplete="off"></div>
        <div class="f-group">
          <label class="f-label">Sort by</label>
          <select id="sort" class="f-select">
            <option value="trusted">Best value (most trusted)</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="confidence">Confidence score</option>
            <option value="name">Name (A–Z)</option>
          </select>
        </div>
        <div class="f-group">
          <label class="f-label">Max price <span id="priceVal" class="f-pill">${money(String(priceCeil))}</span></label>
          <input id="price" type="range" min="0" max="${priceCeil}" step="50" value="${priceCeil}" class="f-range">
        </div>
        <div class="f-group">
          <label class="f-label">Minimum confidence</label>
          <select id="grade" class="f-select">
            <option value="">Any grade</option>
            <option value="A">A only</option><option value="B">B or better</option>
            <option value="C">C or better</option><option value="D">D or better</option>
          </select>
        </div>
        <div class="f-group">
          <label class="f-label">Condition</label>
          ${allConds.map(c => `<label class="f-check"><input type="checkbox" data-cond="${esc(c)}">${esc(COND_LABEL[c] || c)}</label>`).join('')}
        </div>
        <div class="f-group">
          <label class="f-label">Retailer</label>
          ${allRetailers.map(r => `<label class="f-check"><input type="checkbox" data-retailer="${esc(r)}">${esc(RETAILER_LABEL[r] || r)}</label>`).join('')}
        </div>
        <button class="f-clear" id="clear">Clear filters</button>
      </aside>
      <section class="browse-main">
        <div class="browse-bar"><span id="count"></span></div>
        <div id="grid" class="prod-grid"></div>
      </section>
    </div>`;

  const grid = document.getElementById('grid')!;
  const count = document.getElementById('count')!;
  const apply = (): void => {
    let list = products.filter(p => {
      if (state.q && !(`${p.name} ${p.model} ${p.variant || ''}`.toLowerCase().includes(state.q.toLowerCase()))) return false;
      if (Number(p.best_price || p.price_min || 0) > state.maxPrice) return false;
      if (state.minGrade && (GRADE_RANK[p.best_grade || 'F'] || 0) < (GRADE_RANK[state.minGrade] || 0)) return false;
      if (state.conditions.size && !p.conditions.some(c => state.conditions.has(c))) return false;
      if (state.retailers.size && !p.retailers.some(r => state.retailers.has(r))) return false;
      return true;
    });
    list = sortItems(list, state.sort);
    count.textContent = `${list.length} of ${products.length} models`;
    grid.innerHTML = list.length ? list.map(browseCard).join('') : `<div class="loading">No MacBooks match these filters. <button class="linklike" id="reset">Reset</button></div>`;
    document.getElementById('reset')?.addEventListener('click', () => { (document.getElementById('clear') as HTMLButtonElement).click(); });
  };

  (document.getElementById('q') as HTMLInputElement).addEventListener('input', e => { state.q = (e.target as HTMLInputElement).value; apply(); });
  (document.getElementById('sort') as HTMLSelectElement).addEventListener('change', e => { state.sort = (e.target as HTMLSelectElement).value; apply(); });
  (document.getElementById('grade') as HTMLSelectElement).addEventListener('change', e => { state.minGrade = (e.target as HTMLSelectElement).value; apply(); });
  const priceEl = document.getElementById('price') as HTMLInputElement;
  priceEl.addEventListener('input', e => { state.maxPrice = Number((e.target as HTMLInputElement).value); document.getElementById('priceVal')!.textContent = money(String(state.maxPrice)); apply(); });
  app.querySelectorAll<HTMLInputElement>('[data-cond]').forEach(cb => cb.addEventListener('change', () => { cb.checked ? state.conditions.add(cb.dataset.cond!) : state.conditions.delete(cb.dataset.cond!); apply(); }));
  app.querySelectorAll<HTMLInputElement>('[data-retailer]').forEach(cb => cb.addEventListener('change', () => { cb.checked ? state.retailers.add(cb.dataset.retailer!) : state.retailers.delete(cb.dataset.retailer!); apply(); }));
  document.getElementById('clear')!.addEventListener('click', () => {
    state.q = ''; state.sort = 'trusted'; state.conditions.clear(); state.retailers.clear(); state.maxPrice = priceCeil; state.minGrade = '';
    (document.getElementById('q') as HTMLInputElement).value = '';
    (document.getElementById('sort') as HTMLSelectElement).value = 'trusted';
    (document.getElementById('grade') as HTMLSelectElement).value = '';
    priceEl.value = String(priceCeil); document.getElementById('priceVal')!.textContent = money(String(priceCeil));
    app.querySelectorAll<HTMLInputElement>('[data-cond],[data-retailer]').forEach(cb => (cb.checked = false));
    apply();
  });
  apply();
}

function sortItems(list: CategoryItem[], sort: string): CategoryItem[] {
  const arr = [...list];
  if (sort === 'price_asc') return arr.sort((a, b) => Number(a.best_price || a.price_min || 1e9) - Number(b.best_price || b.price_min || 1e9));
  if (sort === 'price_desc') return arr.sort((a, b) => Number(b.best_price || b.price_max || 0) - Number(a.best_price || a.price_max || 0));
  if (sort === 'confidence') return arr.sort((a, b) => (b.best_score || 0) - (a.best_score || 0));
  if (sort === 'name') return arr.sort((a, b) => a.name.localeCompare(b.name));
  // 'trusted' (best value): trusted score per dollar — reward high confidence at low price
  return arr.sort((a, b) => valueScore(b) - valueScore(a));
}
function valueScore(p: CategoryItem): number {
  const price = Number(p.best_price || p.price_min || 0);
  const score = p.best_score || 0;
  return price > 0 ? (score * 1000) / price : 0;
}

function browseCard(p: CategoryItem): string {
  const g = p.best_grade || '?';
  const conds = [...p.conditions].sort((a, b) => COND_ORDER.indexOf(a) - COND_ORDER.indexOf(b));
  return `
  <a class="prod-card" href="/shop/laptops/${esc(p.slug)}" data-spa>
    <div class="pc-img">${p.image_url ? `<img src="${esc(p.image_url)}" alt="${esc(p.name)}" loading="lazy" onerror="this.closest('.pc-img').classList.add('pc-noimg');this.remove()">` : `<span>${esc(p.brand)}</span>`}
      ${p.best_grade ? `<span class="pc-grade grade-${g.toLowerCase()}" title="Best Confidence Score ${p.best_score}/100">${esc(g)}</span>` : ''}
    </div>
    <div class="pc-body">
      <div class="pc-name">${esc(p.model)}</div>
      <div class="pc-variant">${esc(p.variant || '')}</div>
      <div class="pc-conds">${conds.slice(0, 4).map(c => `<span class="cc cond-${esc(c)}">${esc(COND_LABEL[c] || c)}</span>`).join('')}</div>
      <div class="pc-foot">
        <span class="pc-from">from <b>${money(p.best_price)}</b></span>
        <span class="pc-count">${p.offer_count} offers</span>
      </div>
    </div>
  </a>`;
}

// ---- product truth page ----------------------------------------------------
async function renderProduct(slug: string): Promise<void> {
  const p = await api<ProductView>(`/api/shop/product/${encodeURIComponent(slug)}`);
  const msrp = p.msrp_usd ? Number(p.msrp_usd) : null;
  const lt = p.lowest_trusted;

  app.innerHTML = `
    <a class="back" href="/shop/${esc(p.category)}" data-spa>← all ${esc(p.category)}</a>
    <header class="truth-hero">
      <div class="th-media">${p.image_url ? `<img src="${esc(p.image_url)}" alt="${esc(p.name)}" onerror="this.parentElement.classList.add('noimg')">` : ''}</div>
      <div class="th-info">
        <div class="th-brand">${esc(p.brand)}</div>
        <h1>${esc(p.model)} <span class="th-variant">${esc(p.variant || '')}</span></h1>
        ${p.summary ? `<div class="th-summary"><span class="th-summary-tag">What the product page won't tell you</span><p>${esc(p.summary)}</p></div>` : ''}
        <div class="th-anchors">
          ${msrp ? `<div class="anchor"><span>MSRP</span><b>${money(p.msrp_usd)}</b></div>` : ''}
          <div class="anchor"><span>Lowest price</span><b>${money(p.best_price)}</b></div>
          ${lt ? `<div class="anchor trusted"><span>Lowest <em>trusted</em> (${lt.grade})</span><b>${money(lt.price_usd)}</b><small>${esc(RETAILER_LABEL[lt.retailer] || lt.retailer)} · ${esc(COND_LABEL[lt.condition] || lt.condition)}</small></div>` : ''}
        </div>
        ${lt && p.best_price && Number(p.best_price) < Number(lt.price_usd)
          ? `<p class="th-warn">⚠ The cheapest sticker (${money(p.best_price)}) is <b>not</b> the one we trust. The lowest <em>trusted</em> offer is ${money(lt.price_usd)} — the gap buys away warranty, battery health, or authenticity. See why below.</p>` : ''}
      </div>
    </header>
    <section class="offers">
      <div class="offers-head"><h2>${p.offers.length} offers, ranked by our Confidence Score</h2><span class="offers-sub">Highest trust first — not lowest price. Faults shown up front.</span></div>
      ${p.offers.map(o => offerCard(o, msrp)).join('')}
    </section>`;
}

function offerCard(o: OfferView, msrp: number | null): string {
  const grade = o.grade || '?';
  const vsMsrp = msrp && o.price_usd ? Math.round((1 - Number(o.price_usd) / msrp) * 100) : null;
  const faults = [...o.faults].sort((a, b) => sev(b.severity) - sev(a.severity));
  const badFaults = faults.filter(f => f.severity === 'critical' || f.severity === 'major');
  const buyUrl = o.affiliate_url || o.url;
  return `
  <article class="offer grade-${grade.toLowerCase()}">
    <div class="of-main">
      <div class="of-score" title="Confidence Score ${o.score ?? '—'}/100"><span class="grade">${esc(grade)}</span><span class="score">${o.score ?? '—'}</span></div>
      <div class="of-id">
        <div class="of-retailer">${esc(RETAILER_LABEL[o.retailer] || o.retailer)}
          <span class="cond cond-${esc(o.condition)}">${esc(COND_LABEL[o.condition] || o.condition)}${o.condition_grade ? ` · ${esc(o.condition_grade)}` : ''}</span>
        </div>
        <div class="of-meta">
          ${o.seller ? `<span>${esc(o.seller)}</span>` : ''}${o.seller_rating ? `<span>· ${esc(o.seller_rating)}</span>` : ''}
          <span class="prov prov-${esc(o.crawl_source)}" title="how we sourced this">${o.crawl_source === 'api' ? 'verified via API' : 'crawled'}</span>
        </div>
      </div>
      <div class="of-price">
        <b>${money(o.price_usd)}</b>
        ${vsMsrp && vsMsrp > 0 ? `<span class="off">${vsMsrp}% off MSRP</span>` : (vsMsrp != null && vsMsrp < 0 ? `<span class="over">${-vsMsrp}% over MSRP</span>` : '')}
        ${!o.in_stock ? `<span class="oos">out of stock</span>` : ''}
      </div>
      <a class="of-buy" href="${esc(buyUrl)}" target="_blank" rel="nofollow noopener sponsored">View at ${esc(RETAILER_LABEL[o.retailer] || o.retailer)} →</a>
    </div>
    ${faults.length ? `
    <div class="of-faults">
      <div class="ff-head">${badFaults.length ? `🚩 ${badFaults.length} thing${badFaults.length > 1 ? 's' : ''} they don't put in the title` : 'Know before you buy'}</div>
      <ul>${faults.map(f => `<li class="fault sev-${esc(f.severity)}"><span class="fdot"></span><div><b>${esc(f.title)}</b>${f.verified ? ' <span class="verified">✓ verified</span>' : ' <span class="unverified">unverified</span>'}<p>${esc(f.detail)}</p></div></li>`).join('')}</ul>
    </div>` : ''}
    ${o.claims.length ? `
    <div class="of-claims">
      ${o.claims.map(c => `<div class="claim delta-${esc(c.delta)}"><div class="cl-row"><span class="cl-said">They say: “${esc(c.claim_text)}”</span><span class="cl-tag">${esc(DELTA_LABEL[c.delta] || c.delta)}</span></div>${c.reality ? `<div class="cl-real"><b>Reality:</b> ${esc(c.reality)}</div>` : ''}${c.note ? `<div class="cl-note">${esc(c.note)}</div>` : ''}</div>`).join('')}
    </div>` : ''}
    <div class="of-foot">${o.warranty ? `<span><b>Warranty:</b> ${esc(o.warranty)}</span>` : ''}${o.returns ? `<span><b>Returns:</b> ${esc(o.returns)}</span>` : ''}</div>
  </article>`;
}

function sev(s: string): number { return s === 'critical' ? 4 : s === 'major' ? 3 : s === 'minor' ? 2 : 1; }

route();
